/**
 * Analytics helpers — thin, fault-tolerant wrappers around Google Analytics 4
 * (gtag.js, loaded globally in index.html). All functions are no-ops when GA
 * hasn't loaded yet, so partial-load / privacy-blocked sessions never throw.
 *
 * Events use GA4 custom-event names so they can be consumed in GA4 Explorations,
 * Remarketing audiences, and Google Ads conversions/retargeting lists.
 */

function safeGtag() {
  if (typeof window === 'undefined') return null;
  return typeof window.gtag === 'function' ? window.gtag : null;
}

/** Fire a generic GA4 custom event. */
export function trackEvent(name, params = {}) {
  const gtag = safeGtag();
  if (!gtag) return;
  try {
    gtag('event', name, params);
  } catch {
    /* ignore — never let analytics break UX */
  }
}

/** Manual page_view (needed for SPA route changes, which GA4 won't catch automatically). */
export function trackPageView(path, title) {
  const gtag = safeGtag();
  if (!gtag) return;
  try {
    gtag('event', 'page_view', {
      page_path: path || (window.location ? window.location.pathname + window.location.search : '/'),
      page_title: title || (document ? document.title : undefined),
    });
  } catch {
    /* ignore */
  }
}

/** A user started interacting with a lead-capture form (first field focus). */
export function trackFormStart(formName) {
  trackEvent('form_start', { form_name: formName });
}

/** A form was successfully submitted. */
export function trackFormSubmit(formName) {
  trackEvent('form_submit', { form_name: formName });
}

/** A "Get demo / book demo" call-to-action was clicked. */
export function trackDemoClick(location) {
  trackEvent('demo_click', { cta_location: location });
}

/** A CTA that routes the user to the contact form was clicked (e.g. a pricing plan button). */
export function trackContactClick(location) {
  trackEvent('contact_click', { cta_location: location });
}

/** Scroll depth milestone reached (25 / 50 / 75 / 100). */
export function trackScrollDepth(percent, pagePath) {
  trackEvent('scroll_depth', { scroll_percent: percent, page_path: pagePath });
}